import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTr } from "@/autoTranslate";
import { useSiteContent } from "@/siteContent";

const ACCENTS = ["var(--coral)", "var(--cobalt)", "var(--chartreuse)"];

export default function Experience() {
  const tr = useTr();
  const site = useSiteContent();
  const jobs = site.getList("works.list");
  const [active, setActive] = useState(0);
  const job = jobs[active] || jobs[0] || {};
  const accent = job.accent || ACCENTS[active % ACCENTS.length];
  const num = String(active + 1).padStart(2, "0");

  return (
    <section
      id="works"
      data-testid="experience-section"
      className="relative px-6 md:px-10 py-8 md:py-12"
    >
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="mt-10 md:mt-16 display-sans text-6xl md:text-[10vw] leading-[0.9]"
      >
        {tr(site.get("works.title"))}
      </motion.h2>

      <div className="mt-12 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
        {/* left: index */}
        <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <div className="border-t border-[color:var(--hair)]">
            {jobs.map((j, i) => {
              const n = String(i + 1).padStart(2, "0");
              const a = j.accent || ACCENTS[i % ACCENTS.length];
              return (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`work-item-${i}`}
                  data-cursor="hover"
                  data-cursor-label="open"
                  className="w-full text-left border-b border-[color:var(--hair)] py-4 md:py-6 flex items-baseline gap-4 group"
                >
                  <span className="chapter-num text-lg md:text-xl w-8 shrink-0">
                    {n}
                  </span>
                  <span
                    className={`display-sans text-xl md:text-3xl transition-all duration-500 ${
                      active === i
                        ? "translate-x-2"
                        : "opacity-50 group-hover:opacity-80"
                    }`}
                    style={active === i ? { color: a, fontStyle: "italic" } : {}}
                  >
                    {tr(j.role)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* right: details */}
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div className="flex items-center justify-between font-mono-label text-[11px] uppercase tracking-widest opacity-70">
            <span>{job.period}</span>
            <span>{tr(job.location)}</span>
          </div>

          <div className="mt-4">
            <div
              className="inline-block px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-mono-label"
              style={{ background: accent, color: "var(--ink)" }}
            >
              {num} — {tr(job.org)}
            </div>
          </div>

          <h3 className="mt-4 display-serif italic text-4xl md:text-6xl leading-[0.95]">
            {tr(job.role)}
          </h3>

          <ul className="mt-8 space-y-4 text-base md:text-lg leading-relaxed">
            {(job.bullets || []).map((b, i) => (
              <li key={i} className="flex gap-4">
                <span
                  className="mt-3 inline-block w-3 h-[2px]"
                  style={{ background: accent }}
                />
                <span>{tr(b)}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-2">
            {(job.tags || []).map((t, i) => (
              <span key={i} className="sticker">
                {tr(t)}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
